import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { catalogoPlanosService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import '../styles/CatalogoPlanos.css';

const CatalogoPlanos = () => {
  const { isAdminOrDiretor } = useAuth();
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [manage, setManage] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ titulo: '', descricao: '', ordem: 0, ativo: true, imagem: null });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const loadItens = async () => {
    try {
      setLoading(true);
      const data = await catalogoPlanosService.list(isAdminOrDiretor() && manage);
      setItens(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao carregar catálogo:', error);
      setItens([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItens();
  }, [manage]);

  const openNovo = () => {
    setEditingId(null);
    setForm({ titulo: '', descricao: '', ordem: 0, ativo: true, imagem: null });
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      titulo: item.titulo,
      descricao: item.descricao || '',
      ordem: item.ordem || 0,
      ativo: item.ativo !== false,
      imagem: null
    });
    setFormError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormError('');
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.titulo.trim()) {
      setFormError('Título é obrigatório.');
      return;
    }
    try {
      setSaving(true);
      if (editingId) {
        await catalogoPlanosService.update(editingId, {
          titulo: form.titulo.trim(),
          descricao: form.descricao.trim() || null,
          ordem: form.ordem,
          ativo: form.ativo,
          imagem: form.imagem || undefined
        });
      } else {
        await catalogoPlanosService.create({
          titulo: form.titulo.trim(),
          descricao: form.descricao.trim() || null,
          ordem: form.ordem,
          ativo: form.ativo,
          imagem: form.imagem || undefined
        });
      }
      closeForm();
      loadItens();
    } catch (error) {
      setFormError(error.response?.data?.error || 'Erro ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este item do catálogo?')) return;
    try {
      setDeletingId(id);
      await catalogoPlanosService.delete(id);
      loadItens();
    } catch (error) {
      alert(error.response?.data?.error || 'Erro ao excluir');
    } finally {
      setDeletingId(null);
    }
  };

  const imageUrl = (item) => {
    if (!item.imagem_url) return null;
    return catalogoPlanosService.getImageUrl(item.imagem_url);
  };

  return (
    <Layout>
      <div className="catalogo-planos-page">
        <div className="page-header">
          <h1>Catálogo de Planos</h1>
          <p>Pacotes e ofertas em destaque para vendedores e supervisores.</p>
        </div>

        {isAdminOrDiretor() && (
          <div className="catalogo-admin-toolbar">
            <label className="catalogo-manage-toggle">
              <input
                type="checkbox"
                checked={manage}
                onChange={(e) => setManage(e.target.checked)}
              />
              Gerenciar (ver inativos e editar)
            </label>
            <button type="button" className="btn-primary" onClick={openNovo}>
              Novo item
            </button>
          </div>
        )}

        {loading ? (
          <div className="loading">Carregando...</div>
        ) : itens.length === 0 ? (
          <p className="catalogo-empty">Nenhum item no catálogo.</p>
        ) : (
          <div className="catalogo-grid">
            {itens.map((item) => (
              <div key={item.id} className={`catalogo-card ${item.ativo === false ? 'catalogo-card-inativo' : ''}`}>
                {imageUrl(item) && (
                  <div className="catalogo-card-image">
                    <img src={imageUrl(item)} alt={item.titulo} />
                  </div>
                )}
                <div className="catalogo-card-body">
                  <h3 className="catalogo-card-titulo">{item.titulo}</h3>
                  {item.descricao && <p className="catalogo-card-descricao">{item.descricao}</p>}
                  {isAdminOrDiretor() && manage && (
                    <div className="catalogo-card-actions">
                      <button type="button" className="btn-primary btn-sm" onClick={() => openEdit(item)}>
                        Editar
                      </button>
                      <button
                        type="button"
                        className="btn-secondary btn-sm btn-danger"
                        onClick={() => handleDelete(item.id)}
                        disabled={deletingId === item.id}
                      >
                        {deletingId === item.id ? 'Excluindo...' : 'Excluir'}
                      </button>
                      {item.ativo === false && <span className="badge-inativo">Inativo</span>}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {showForm && (
          <div className="modal-overlay" onClick={closeForm}>
            <div className="modal-content catalogo-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingId ? 'Editar item' : 'Novo item do catálogo'}</h2>
                <button type="button" className="modal-close" onClick={closeForm}>✕</button>
              </div>
              <form onSubmit={handleSubmit} className="modal-body">
                {formError && <p className="catalogo-form-error">{formError}</p>}
                <div className="form-group">
                  <label>Título *</label>
                  <input
                    type="text"
                    value={form.titulo}
                    onChange={(e) => handleChange('titulo', e.target.value)}
                    className="form-control"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Descrição</label>
                  <textarea
                    value={form.descricao}
                    onChange={(e) => handleChange('descricao', e.target.value)}
                    className="form-control"
                    rows={4}
                  />
                </div>
                <div className="form-group">
                  <label>Imagem</label>
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={(e) => handleChange('imagem', e.target.files[0] || null)}
                    className="form-control"
                  />
                  {editingId && <span className="form-hint">Deixe em branco para manter a imagem atual.</span>}
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Ordem</label>
                    <input
                      type="number"
                      value={form.ordem}
                      onChange={(e) => handleChange('ordem', parseInt(e.target.value, 10) || 0)}
                      className="form-control"
                      min={0}
                    />
                  </div>
                  {isAdminOrDiretor() && (
                    <div className="form-group form-group-checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={form.ativo}
                          onChange={(e) => handleChange('ativo', e.target.checked)}
                        />
                        Ativo
                      </label>
                    </div>
                  )}
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={closeForm}>Cancelar</button>
                  <button type="submit" className="btn-primary" disabled={saving}>
                    {saving ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CatalogoPlanos;
